/**
 * [SC] - 3rd Nov 2026 - Page speed helpers, loaded with defer on every page.
 *
 * Nothing here changes how a page looks once it has loaded. It only changes WHEN
 * heavy or off-screen things load:
 *  - section background images     [data-sc-bg]
 *  - background videos             video[data-sc-lazy-video]
 *  - embedded video players        iframe[data-sc-src]
 *  - collection banner videos      video[data-sc-src]
 *  - hero slides after the first   img[data-sc-srcset]
 *  - product grid hover images      img[data-sc-hover-src]
 *  - project gallery photos        .slider.flexslider img[loading="lazy"]
 */
(function () {
  'use strict';

  var hasObserver = 'IntersectionObserver' in window;

  /** Calls callback(element) once, when the element comes within `margin` of the viewport. */
  function whenNearViewport(element, callback, margin) {
    if (!hasObserver) {
      callback(element);
      return;
    }
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          observer.disconnect();
          callback(element);
        }
      });
    }, { rootMargin: margin || '800px 0px' });
    observer.observe(element);
  }

  /* Expose it for sections that lazy-start their own scripts (the logo marquee). */
  window.scWhenNearViewport = whenNearViewport;

  /* ---------------------------------------------------------------------------
     Section background images
     The markup carries the URL in data-sc-bg. It is only requested when the section
     is close to the viewport, at the width this visitor's screen actually needs.
     --------------------------------------------------------------------------- */
  var BACKGROUND_WIDTHS = [400, 600, 800, 1000, 1200, 1500, 1800, 2000, 2400, 3000];

  function loadBackground(element) {
    var url = element.getAttribute('data-sc-bg');
    var ratio = parseFloat(element.getAttribute('data-sc-bg-ratio')) || 1;
    var box = element.getBoundingClientRect();
    var density = Math.min(window.devicePixelRatio || 1, 2);
    /* background-size: cover has to fill both the width and the height of the box */
    var needed = Math.max(box.width, box.height * ratio) * density;
    var width = BACKGROUND_WIDTHS[BACKGROUND_WIDTHS.length - 1];
    for (var i = 0; i < BACKGROUND_WIDTHS.length; i++) {
      if (BACKGROUND_WIDTHS[i] >= needed) {
        width = BACKGROUND_WIDTHS[i];
        break;
      }
    }
    element.style.backgroundImage = 'url("' + url.replace(/([?&]width=)\d+/, '$1' + width) + '")';
    element.removeAttribute('data-sc-bg');
  }

  function prepareBackgrounds(root) {
    root.querySelectorAll('[data-sc-bg]').forEach(function (element) {
      whenNearViewport(element, loadBackground);
    });
  }

  /* ---------------------------------------------------------------------------
     Background videos
     No bytes are requested until the video is close to the viewport. It then plays
     while visible and pauses while scrolled away, which keeps scrolling smooth.
     --------------------------------------------------------------------------- */
  function startVideo(video) {
    var promise = video.play();
    if (promise && promise.catch) {
      promise.catch(function () {});
    }
  }

  function prepareVideos(root) {
    root.querySelectorAll('video[data-sc-lazy-video]').forEach(function (video) {
      whenNearViewport(video, function () {
        var poster = video.getAttribute('data-sc-poster');
        if (poster) {
          video.setAttribute('poster', poster);
        }
        video.setAttribute('preload', 'auto');
        startVideo(video);

        if (!hasObserver) {
          return;
        }
        new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              startVideo(video);
            } else {
              video.pause();
            }
          });
        }, { rootMargin: '100px 0px' }).observe(video);
      });
    });
  }

  prepareBackgrounds(document);
  prepareVideos(document);

  /* ---------------------------------------------------------------------------
     Embedded video players (Vimeo)
     Each player brings about 1 MB of scripts and third-party cookies, so it loads
     shortly before it scrolls into view. One that is already on screen when the page
     opens waits for the visitor's first interaction, or 12 seconds after the page has
     loaded if there is none, so it doesn't compete with the page itself.
     Collection banner videos (10 MB or more) work the same way.
     --------------------------------------------------------------------------- */
  var framesReleased = false;
  var waitingFrames = [];

  function loadFrame(frame) {
    frame.src = frame.getAttribute('data-sc-src');
    frame.removeAttribute('data-sc-src');
  }

  function releaseFrames() {
    if (framesReleased) {
      return;
    }
    framesReleased = true;
    ['pointerdown', 'keydown', 'touchstart', 'scroll', 'mousemove'].forEach(function (name) {
      window.removeEventListener(name, releaseFrames);
    });
    waitingFrames.forEach(loadFrame);
    waitingFrames = [];
  }

  function prepareFrames(root) {
    var frames = root.querySelectorAll('iframe[data-sc-src], video[data-sc-src]');
    if (!frames.length) {
      return;
    }
    frames.forEach(function (frame) {
      whenNearViewport(frame, function (element) {
        if (framesReleased) {
          loadFrame(element);
        } else {
          waitingFrames.push(element);
        }
      }, '600px 0px');
    });
    if (!framesReleased) {
      ['pointerdown', 'keydown', 'touchstart', 'scroll', 'mousemove'].forEach(function (name) {
        window.addEventListener(name, releaseFrames, { passive: true });
      });
      var releaseLater = function () { setTimeout(releaseFrames, 12000); };
      if (document.readyState === 'complete') {
        releaseLater();
      } else {
        window.addEventListener('load', releaseLater, { once: true });
      }
    }
  }

  prepareFrames(document);

  /* Sections re-rendered while someone edits them in the theme editor */
  document.addEventListener('shopify:section:load', function (event) {
    prepareBackgrounds(event.target);
    prepareVideos(event.target);
    prepareFrames(event.target);
  });

  /* ---------------------------------------------------------------------------
     Hero slides after the first
     They wait until the first slide (the page's largest element) has loaded, so they
     no longer compete with it, and they are ready before the first slide change.
     --------------------------------------------------------------------------- */
  function loadLaterSlides() {
    document.querySelectorAll('img[data-sc-srcset]').forEach(function (image) {
      image.setAttribute('srcset', image.getAttribute('data-sc-srcset'));
      image.removeAttribute('data-sc-srcset');
    });
  }

  if (document.querySelector('img[data-sc-srcset]')) {
    var firstSlideImage = document.querySelector('img[data-sc-hero-first]');
    if (firstSlideImage && !firstSlideImage.complete) {
      var onFirstSlideDone = function () {
        setTimeout(loadLaterSlides, 300);
      };
      firstSlideImage.addEventListener('load', onFirstSlideDone, { once: true });
      firstSlideImage.addEventListener('error', onFirstSlideDone, { once: true });
    } else {
      setTimeout(loadLaterSlides, 300);
    }
  }

  /* ---------------------------------------------------------------------------
     Product grid hover images
     Only useful on devices that can hover, and only after the page has loaded.
     --------------------------------------------------------------------------- */
  function loadHoverImage(image) {
    var src = image.getAttribute('data-sc-hover-src');
    if (src) {
      image.src = src;
      image.removeAttribute('data-sc-hover-src');
    }
  }

  function prepareHoverImages() {
    document.querySelectorAll('img[data-sc-hover-src]').forEach(function (image) {
      whenNearViewport(image, loadHoverImage, '300px 0px');
    });
  }

  if (document.querySelector('img[data-sc-hover-src]') && window.matchMedia('(hover: hover)').matches) {
    /* If someone hovers before those have loaded, fetch that one right away */
    document.addEventListener('mouseover', function (event) {
      var item = event.target.closest && event.target.closest('.product-list-item');
      var image = item && item.querySelector('img[data-sc-hover-src]');
      if (image) {
        loadHoverImage(image);
      }
    }, { passive: true });

    if (document.readyState === 'complete') {
      prepareHoverImages();
    } else {
      window.addEventListener('load', prepareHoverImages, { once: true });
    }
  }

  /* ---------------------------------------------------------------------------
     Project gallery photos
     Only the first photo loads with the page. The photos on either side of the current
     one are fetched ahead of time, so changing slide still feels instant.
     --------------------------------------------------------------------------- */
  function loadPhotoAt(cells, index) {
    var cell = cells[(index + cells.length) % cells.length];
    if (!cell) {
      return;
    }
    var image = cell.tagName === 'IMG' ? cell : cell.querySelector('img');
    if (image && image.getAttribute('loading') === 'lazy') {
      image.setAttribute('loading', 'eager');
    }
  }

  function preparePhotoGallery(slider) {
    var flickity = window.Flickity && window.Flickity.data(slider);
    if (!flickity) {
      return;
    }
    var preloadNeighbours = function () {
      var cells = flickity.getCellElements();
      loadPhotoAt(cells, flickity.selectedIndex + 1);
      loadPhotoAt(cells, flickity.selectedIndex - 1);
    };
    flickity.on('cellSelect', preloadNeighbours);
    preloadNeighbours();
  }

  function preparePhotoGalleries() {
    document.querySelectorAll('.slider.flexslider, .hc-slider-faq__slider').forEach(preparePhotoGallery);
  }

  if (document.querySelector('.slider.flexslider, .hc-slider-faq__slider')) {
    if (document.readyState === 'complete') {
      preparePhotoGalleries();
    } else {
      window.addEventListener('load', preparePhotoGalleries, { once: true });
    }
  }
})();
